import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { getEvents } from '../services/api'
import type { EventSummary } from '../types/crm'

interface EventContextValue {
  events: EventSummary[]
  selectedEvent: EventSummary | null
  loading: boolean
  selectEvent: (id: string) => void
  refreshEvents: () => Promise<void>
}

const EventContext = createContext<EventContextValue | null>(null)

export function EventProvider({ children }: { children: ReactNode }) {
  const [events, setEvents] = useState<EventSummary[]>([])
  const [selectedId, setSelectedId] = useState(() => localStorage.getItem('digi-selected-event'))
  const [loading, setLoading] = useState(true)

  const refreshEvents = useCallback(async () => {
    setLoading(true)
    const items = await getEvents()
    setEvents(items)
    setSelectedId((current) => current ?? items[0]?.id ?? null)
    setLoading(false)
  }, [])

  useEffect(() => { void refreshEvents() }, [refreshEvents])
  useEffect(() => {
    if (selectedId) localStorage.setItem('digi-selected-event', selectedId)
  }, [selectedId])

  const value = useMemo(() => ({
    events, loading, refreshEvents,
    selectedEvent: events.find((event) => event.id === selectedId) ?? events[0] ?? null,
    selectEvent: setSelectedId,
  }), [events, loading, refreshEvents, selectedId])

  return <EventContext.Provider value={value}>{children}</EventContext.Provider>
}

export function useEventContext() {
  const value = useContext(EventContext)
  if (!value) throw new Error('useEventContext doit être utilisé dans EventProvider')
  return value
}
